import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import api from "../../api/api";
import usePortalChat from '../../../hooks/usePortalChat';
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import Toolbar from "../base/Toolbar";
import DataTable from "../base/DataTable";
import ColumnsModal from "../base/ColumnsModal";
import FiltersModal from "../base/FiltersModal";
import { filtersConfig } from "../config/filtersConfig";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Stack,
  Chip,
  TablePagination,
  Pagination,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Upload as UploadIcon,
  ChatBubbleOutline as ChatIcon,
} from "@mui/icons-material";

// Ключ для сохранения состояния таблицы в localStorage
const TABLE_STATE_KEY = "table_state_clients";

// Функция для загрузки сохраненного состояния таблицы
const loadTableState = () => {
  try {
    const saved = localStorage.getItem(TABLE_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Ошибка загрузки состояния таблицы:", error);
  }
  return null;
};

// Функция для сохранения состояния таблицы
const saveTableState = (state) => {
  try {
    localStorage.setItem(TABLE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Ошибка сохранения состояния таблицы:", error);
  }
};

// Конфигурация колонок для клиентов
const clientsConfig = [
  { field: "id", label: "ID", sortable: true },
  { field: "client_number", label: "Номер клиента", sortable: true },
  { field: "display_name", label: "Полное имя", sortable: true },
  { field: "contact_info", label: "Основной контакт", sortable: true },
  { field: "debt", label: "Долг", sortable: true },
  { field: "black_list", label: "Черный список", sortable: true },
  {
    field: "attraction_source_name",
    label: "Источник привлечения",
    sortable: true,
  },
  { field: "assistant_status", label: "Статус ассистента", sortable: true },
  { field: "created_at", label: "Дата создания", sortable: true },
  { field: "client_type", label: "Тип клиента", sortable: true },
  { field: "phone", label: "Дополнительный телефон", sortable: true },
  { field: "address", label: "Адрес", sortable: false },
  { field: "discount_percent", label: "Скидка (%)", sortable: true },
  { field: "total_rental_amount", label: "Сумма аренд", sortable: true },
  { field: "total_deals_count", label: "Кол-во сделок", sortable: true },
];

// Действия для клиентов
const clientActions = [
  {
    label: "Открыть/Редактировать",
    onClick: (id, handlers) => {
      // Сохраняем ID клиента для скролла при возврате
      localStorage.setItem("lastViewedClientId", id.toString());
      if (handlers && handlers.navigate) {
        handlers.navigate(`/clients/edit/${id}`);
      } else {
        window.location.href = `/clients/edit/${id}`;
      }
    },
    color: "primary",
    condition: (row) => true,
    tooltip: "Открыть или редактировать клиента",
  },
  {
    label: "Копировать",
    onClick: (id, handlers) => handlers.onCopy(id),
    color: "blue",
    condition: (row) => true,
    tooltip: "Скопировать этого клиента",
  },
  {
    label: "Удалить",
    onClick: (id, handlers) => handlers.onDelete(id),
    color: "red",
    condition: (row) => !row.is_deleted,
    tooltip: "Удалить клиента",
  },
  {
    label: "Восстановить",
    onClick: (id, handlers) => handlers.onRestore(id),
    color: "green",
    condition: (row) => row.is_deleted,
    tooltip: "Восстановить удалённого клиента",
  },
];

const ClientsPage = () => {
  const { openChat, opening: chatOpening, error: chatError } = usePortalChat();
  // Загружаем сохраненное состояние
  const savedState = loadTableState();

  const [clients, setClients] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  // const [businessTypeKey, setBusinessTypeKey] = useState(null); // Убрано - больше не нужно
  const [searchQuery, setSearchQuery] = useState("");
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [loading, setLoading] = useState(false);

  // Новая пагинация (с восстановлением из localStorage)
  const [page, setPage] = useState(savedState?.page || 1);
  const [pageSize, setPageSize] = useState(savedState?.pageSize || 20);
  const [totalClients, setTotalClients] = useState(0);
  const totalPages = Math.ceil(totalClients / pageSize);

  // Сортировка (с восстановлением из localStorage)
  const [sortBy, setSortBy] = useState(
    savedState?.sortField || "client_number",
  );
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder || "desc");

  // ID элемента для подсветки и скролла после возврата
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    clientId: null,
  });
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [attractionSources, setAttractionSources] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { handleError, showSuccess } = useErrorHandler();
  const navigate = useNavigate();
  const tableKey = "clients";
  const filtersConfigForClients = filtersConfig[tableKey] || [];

  // Сохраняем состояние таблицы при изменении
  useEffect(() => {
    saveTableState({ page, pageSize, sortField: sortBy, sortOrder });
  }, [page, pageSize, sortBy, sortOrder]);

  // Скролл к последнему просмотренному элементу после загрузки данных
  useEffect(() => {
    const lastViewedId = localStorage.getItem("lastViewedClientId");
    if (lastViewedId && clients.length > 0 && !loading) {
      const id = parseInt(lastViewedId, 10);
      // Проверяем, есть ли элемент в текущем списке
      const elementExists = clients.some((client) => client.id === id);
      if (elementExists) {
        setHighlightedRowId(id);
        // Скроллим к элементу с небольшой задержкой для рендера
        setTimeout(() => {
          const element = document.getElementById(`clients-row-${id}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
        // Убираем подсветку через 2 секунды
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
      // Очищаем сохраненный ID
      localStorage.removeItem("lastViewedClientId");
    }
  }, [clients, loading]);

  // Загрузка клиентов
  const fetchClients = async (
    query = "",
    filters = {},
    currentPage = 1,
    currentPageSize = 50,
    currentSortBy = sortBy,
    currentSortOrder = sortOrder,
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Добавляем пагинацию
      params.append("page", currentPage);
      params.append("limit", currentPageSize);

      // Добавляем сортировку
      if (currentSortBy) {
        params.append("sort_by", currentSortBy);
        params.append("sort_order", currentSortOrder);
      }

      // Добавляем поисковый запрос
      if (query && query.trim()) {
        params.append("search", query.trim());
      }

      // Добавляем фильтры
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== "") {
          params.append(key, value);
        }
      });

      const response = await api.get("/clients/", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Проверяем формат ответа (пагинированный или нет)
      if (response.data.items) {
        // Пагинированный ответ
        setClients(response.data.items);
        setTotalClients(response.data.total);
      } else {
        // Старый формат (массив)
        setClients(response.data);
        setTotalClients(response.data.length);
      }
    } catch (error) {
      console.error("Ошибка загрузки клиентов:", error);
      handleError(error, "Не удалось загрузить список клиентов.");
    } finally {
      setLoading(false);
    }
  };

  // Загрузка колонок
  const fetchColumns = async () => {
    try {
      const response = await api.get("/table-columns/", {
        params: { table_key: "clients" },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Преобразуем колонки из базы данных
      const dbColumns = response.data;

      const transformedColumns = dbColumns.map((col) => ({
        id: col.id,
        table_key: col.table_key,
        column_name: col.column_name,
        field: col.column_name,
        label: translateLabelToRussian(col.column_name),
        sortable: col.sortable,
        is_visible: col.is_visible,
      }));

      setColumns(transformedColumns);
    } catch (error) {
      console.error("❌ Ошибка загрузки колонок клиентов:", error);
      handleError(error, "Не удалось загрузить конфигурацию таблицы клиентов.");

      // В случае ошибки используем статическую конфигурацию
      const fallbackColumns = clientsConfig.map((config, index) => ({
        id: index,
        table_key: "clients",
        column_name: config.field,
        field: config.field,
        label: config.label,
        sortable: config.sortable,
        is_visible: true,
      }));
      setColumns(fallbackColumns);
      console.log(
        "🛡️ Используем fallback конфигурацию для клиентов:",
        fallbackColumns,
      );
    }
  };

  // Функция для перевода на русский язык
  const translateLabelToRussian = (columnName) => {
    const translations = {
      id: "ID",
      client_number: "Номер клиента",
      name: "Полное имя",
      contact_info: "Основной контакт",
      debt: "Долг",
      black_list: "Черный список",
      attraction_source_id: "Источник привлечения",
      attraction_source_name: "Источник привлечения",
      assistant_status: "Статус ассистента",
      created_at: "Дата создания",
      client_type: "Тип клиента",
      first_name: "Имя",
      last_name: "Фамилия",
      middle_name: "Отчество",
      birth_date: "Дата рождения",
      gender: "Пол",
      comment: "Комментарий",
      passport_series: "Серия паспорта",
      passport_number: "Номер паспорта",
      passport_issued_by: "Кем выдан паспорт",
      passport_issued_date: "Дата выдачи паспорта",
      passport_division_code: "Код подразделения",
      inn: "ИНН",
      no_contract: "Без договора",
      no_deposit: "Без залога",
      sms_notifications: "SMS уведомления",
      phone: "Дополнительный телефон",
      email: "Email",
      address: "Адрес",
      discount_percent: "Скидка (%)",
      company_id: "ID компании",
      telegram_id: "ID Telegram",
      updated_at: "Дата обновления",
    };
    return translations[columnName] || columnName;
  };

  // Загрузка источников привлечения
  const fetchAttractionSources = async () => {
    try {
      const response = await api.get("/clients/attraction-sources/list", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setAttractionSources(response.data);
      return response.data;
    } catch (error) {
      console.error("Ошибка загрузки источников привлечения:", error);
      return [];
    }
  };

  // Инициализация фильтров
  const initializeFilters = async () => {
    try {
      // Загружаем источники привлечения
      const sources = await fetchAttractionSources();

      // Преобразуем в формат для FiltersModal
      const filters = filtersConfigForClients.map((filter) => {
        let options = filter.options || [];

        // Если это фильтр источников привлечения, заполняем опции
        if (filter.field === "attraction_source_id") {
          options = sources.map((source) => ({
            value: source.id.toString(),
            label: source.name,
          }));
        }

        return {
          filter_name: filter.field,
          filter_label: filter.label,
          filter_type: filter.type,
          options: options,
          is_visible: filter.isVisible,
          table_key: tableKey,
        };
      });

      setFilters(filters);
    } catch (error) {
      console.error("❌ Ошибка инициализации фильтров клиентов:", error);
    }
  };
  // Загрузка бизнес-ключа - больше не нужна
  /*
  const fetchBusinessTypeKey = async () => {
    try {
      const response = await api.get("/settings/settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.business_type_key) {
        setBusinessTypeKey(response.data.business_type_key);
      }
    } catch (error) {
      console.error("Ошибка загрузки ключа бизнеса:", error);
    }
  };
  */

  // Обработчик сортировки
  const handleSort = (field) => {
    let newSortOrder = "asc";

    if (sortBy === field) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortBy(field);
    setSortOrder(newSortOrder);
    setPage(1);
    fetchClients(searchQuery, activeFilters, 1, pageSize, field, newSortOrder);
  };

  // Копирование клиента
  const handleCopy = (id) => {
    const client = clients.find((c) => c.id === id);
    if (client) {
      // Удаляем id и другие поля, которые не нужны при копировании
      const { id, created_at, updated_at, company_id, ...copyData } = client;

      // Добавляем к имени " (копия)"
      copyData.name = copyData.name ? copyData.name + " (копия)" : "";

      console.log("Copying client:", id, "->", copyData);

      // Передаём данные через state
      navigate("/clients/edit/new", { state: { copyData } });
    }
  };

  // Удаление клиента
  const handleDelete = async (id) => {
    try {
      await api.delete(`/clients/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setPage(1);
      fetchClients(searchQuery, activeFilters, 1, pageSize);
      setDeleteDialog({ open: false, clientId: null });
      showSuccess(`Клиент с ID ${id} удален`);
    } catch (error) {
      console.error("Ошибка удаления клиента:", error);
      handleError(
        error,
        "Не удалось удалить клиента. Возможно, он связан с другими данными.",
      );
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.post(
        `/clients/${id}/restore`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      fetchClients(searchQuery, activeFilters, page, pageSize);
      showSuccess(`Клиент с ID ${id} восстановлен`);
    } catch (error) {
      console.error("Ошибка восстановления клиента:", error);
      handleError(error, "Не удалось восстановить клиента");
    }
  };

  // Удаление выбранных клиентов
  const handleDeleteSelected = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;

      for (let id of selectedItems) {
        try {
          await api.delete(`/clients/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          successCount++;
        } catch (err) {
          console.error(`Ошибка удаления клиента ${id}:`, err);
          errorCount++;
        }
      }

      setSelectedItems([]);
      setPage(1);
      fetchClients(searchQuery, activeFilters, 1, pageSize);

      if (successCount > 0 && errorCount === 0) {
        showSuccess(`Успешно удалено клиентов: ${successCount}`);
      } else if (successCount > 0 && errorCount > 0) {
        handleError(
          new Error(`Удалено: ${successCount}, ошибок: ${errorCount}`),
          `Удалено: ${successCount} клиентов, не удалось удалить: ${errorCount}`,
        );
      } else {
        handleError(
          new Error("Не удалось удалить выбранных клиентов"),
          "Не удалось удалить выбранных клиентов. Возможно, они связаны с другими данными.",
        );
      }
    } catch (error) {
      console.error("Ошибка удаления выбранных клиентов:", error);
      handleError(error, "Произошла ошибка при удалении выбранных клиентов.");
    }
  };
  const handleColumnsChange = async (updatedColumns) => {
    try {
      console.log("Сохраняем настройки колонок для клиентов:", updatedColumns);

      // Преобразуем колонки в формат для bulk update
      const updates = updatedColumns.map((col) => ({
        id: col.id,
        is_visible: col.is_visible,
      }));

      // Отправляем обновления на сервер
      await api.put("/table-columns/bulk-update-visibility", updates, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Обновляем локальное состояние
      setColumns(updatedColumns);
      console.log("✅ Настройки колонок клиентов сохранены");

      showSuccess("Настройки колонок успешно сохранены!");
    } catch (error) {
      console.error("❌ Ошибка сохранения настроек колонок клиентов:", error);
      handleError(error, "Не удалось сохранить настройки колонок.");
    }
  };

  // Обработка применения фильтров
  const handleApplyFilters = async (filterValues) => {
    setActiveFilters(filterValues);
    setPage(1);
    fetchClients(searchQuery, filterValues, 1, pageSize);
  };

  // Сброс фильтров
  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1);
    fetchClients(searchQuery, {}, 1, pageSize);
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchClients(searchQuery, activeFilters, 1, pageSize);
  };

  // Автоматический поиск с задержкой при изменении searchQuery
  useEffect(() => {
    // Пропускаем первый вызов при инициализации
    if (searchQuery === "") return;

    const timeoutId = setTimeout(() => {
      console.log("🔍 Автоматический поиск:", searchQuery);
      setPage(1);
      fetchClients(searchQuery, activeFilters, 1, pageSize);
    }, 500); // Задержка 500мс

    return () => clearTimeout(timeoutId);
  }, [searchQuery]); // Триггер при изменении поискового запроса
  useEffect(() => {
    // fetchBusinessTypeKey(); // Убрано - больше не нужно
    loadSavedFiltersAndFetchClients();
    fetchColumns();
    initializeFilters();
  }, []);

  // Функция для загрузки сохраненных фильтров и применения их к данным
  const loadSavedFiltersAndFetchClients = async () => {
    try {
      // Загружаем сохраненные фильтры пользователя из filter_config
      const response = await api.get(`/filters/${tableKey}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data && response.data.length > 0) {
        // Восстанавливаем значения фильтров из сохраненных данных
        const savedFilters = {};
        const activeFiltersForAPI = {};
        response.data.forEach((filter) => {
          if (
            filter.filter_type === "value" &&
            filter.options &&
            filter.options.length > 0
          ) {
            const value = filter.options[0] ?? "";
            // Сохраняем все значения для отображения в FiltersModal
            savedFilters[filter.filter_name] = value;

            // Добавляем в activeFiltersForAPI только непустые значения (для активной фильтрации)
            if (String(value).trim() !== "") {
              activeFiltersForAPI[filter.filter_name] = value;
            }
          }
        });

        setActiveFilters(savedFilters); // Устанавливаем все фильтры для отображения
        // Загружаем данные только с непустыми фильтрами
        fetchClients(searchQuery, activeFiltersForAPI, page, pageSize);
      } else {
        // Если сохраненных фильтров нет, загружаем данные без фильтров
        fetchClients("", {}, page, pageSize);
      }
    } catch (error) {
      console.error(
        "❌ Ошибка загрузки сохраненных фильтров для клиентов:",
        error,
      );
      // В случае ошибки загружаем данные без фильтров
      fetchClients("", {}, page, pageSize);
    }
  };

  // Обработка импорта клиентов
  const handleImport = async () => {
    if (!importFile) {
      enqueueSnackbar("Выберите файл для импорта", { variant: "warning" });
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await api.post("/clients/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const message =
        response.data.updated > 0
          ? `Импортировано: ${response.data.imported}, Обновлено: ${response.data.updated}, Пропущено: ${response.data.skipped}`
          : `Импортировано: ${response.data.imported}, Пропущено: ${response.data.skipped}`;

      enqueueSnackbar(message, { variant: "success" });

      setImportDialog(false);
      setImportFile(null);
      setPage(1);
      fetchClients(searchQuery, activeFilters, 1, pageSize); // Обновляем список
    } catch (error) {
      handleError(error, "Ошибка импорта клиентов");
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/clients/import/template", {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_clients.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("Шаблон загружен", { variant: "success" });
    } catch (error) {
      handleError(error, "Ошибка загрузки шаблона");
    }
  };

  return (
    <>
      {chatError && <Alert severity="error" sx={{ mb: 2 }}>{chatError}</Alert>}
      <Toolbar
        currentPage="clients"
        onAddClick={() => navigate("/clients/edit/new")}
        onViewChange={(view) => setViewMode(view)}
        onRefresh={() =>
          fetchClients(
            searchQuery,
            activeFilters,
            page,
            pageSize,
            sortBy,
            sortOrder,
          )
        }
        onExport={() => console.log("Экспорт клиентов")}
        onOpenColumnsModal={() => setIsColumnsModalOpen(true)}
        onOpenFiltersModal={() => setIsFiltersModalOpen(true)}
        totalRecords={totalClients}
        recordsLabel="клиентов"
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
          fetchClients(
            searchQuery,
            activeFilters,
            1,
            newSize,
            sortBy,
            sortOrder,
          );
        }}
        onDeleteSelected={handleDeleteSelected}
        selectedItems={selectedItems}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onSearchClear={() => {
          setSearchQuery("");
          setPage(1);
          fetchClients("", activeFilters, 1, pageSize);
        }}
        customButtons={[
          {
            label: "Импорт",
            icon: <UploadIcon />,
            onClick: () => setImportDialog(true),
          },
        ]}
      />
      <DataTable
        selectedItems={selectedItems}
        data={clients}
        config={
          columns.length > 0
            ? columns.filter((col) => col.is_visible)
            : clientsConfig
        }
        viewMode={viewMode}
        sortField={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onToolbarData={({ selectedItems }) => setSelectedItems(selectedItems)}
        actions={[{
          label: chatOpening ? 'Открываем чат…' : 'Написать',
          onClick: id => openChat({ clientId: id }),
          condition: row => !row.is_deleted,
          tooltip: 'Открыть переписку в личном кабинете',
        }, ...clientActions]}
        renderCardActions={client => !client.is_deleted && <Button
          size="small" startIcon={<ChatIcon />} disabled={chatOpening}
          onClick={() => openChat({ clientId: client.id })}
        >Написать</Button>}
        onCopy={handleCopy}
        navigate={navigate}
        onDelete={handleDelete}
        onRestore={handleRestore}
        clients={clients}
        highlightedRowId={highlightedRowId}
        tableKey="clients"
      />

      {/* Панель пагинации */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Показано {clients.length} из {totalClients} клиентов
        </Typography>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => {
              setPage(newPage);
              fetchClients(
                searchQuery,
                activeFilters,
                newPage,
                pageSize,
                sortBy,
                sortOrder,
              );
            }}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>

      <ColumnsModal
        open={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        config={columns}
        onColumnsChange={handleColumnsChange}
        tableKey={tableKey}
      />
      <FiltersModal
        open={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filters={filters || []}
        activeFilters={activeFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        tableKey={tableKey}
      />
      {/* Диалог импорта */}
      <Dialog
        open={importDialog}
        onClose={() => setImportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Импорт клиентов из файла</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Скачать шаблон Excel
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Заполните шаблон и загрузите его для импорта клиентов.
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{ mb: 2, color: "text.secondary" }}
          >
            <strong>Два способа заполнения имени:</strong>
            <br />
            1. Заполните колонку "ФИО" целиком (Иванов Иван Иванович)
            <br />
            2. Или заполните отдельно "Фамилия", "Имя", "Отчество"
            <br />
            <br />
            <strong>Обязательные поля:</strong> ФИО (любой из способов) +
            Телефон
            <br />
            <strong>Дополнительные:</strong> Email, Адрес, Паспортные данные,
            ИНН, Черный список, Долг
          </Typography>

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Выбрать файл
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>

          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Выбран файл: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || importing}
          >
            {importing ? "Импорт..." : "Импортировать"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientsPage;
